// Pre-auths › Closed — the history behind the three queues (00-ds-09 §9, D148).
// A pre-auth leaves "Sent" when the payer answers and leaves "Approved" when it
// is used or lapses; every one of those ends here: used, expired, not sent or
// denied. Grows forever (250–3,600 over two years in the staging review), so it
// pages 50 at a time and defaults to the last 90 days.
//
// Deterministic mock over ~24 months so the demo can actually search, filter and
// page. Real data comes from the server index (§8); the browser never loads a
// whole history in production.

import { CDT, PAYERS, fullName, generate, int, money, pastDate, pick } from '@/components/lists';

export type ClosedOutcome = 'used' | 'expired' | 'not_sent' | 'denied';

export interface ClosedPreauth {
  id: string;
  patient: string;
  payer: string;
  code: string;
  procedure: string;
  tooth: string;
  /** Payer reference — empty for the ones ELVA never sent. */
  reference: string;
  estimate: number;
  outcome: ClosedOutcome;
  /** ISO date the pre-auth closed (used / expired / not sent / denied). */
  closedIso: string;
  /** US label, e.g. "Jan 5, 2026". */
  closedLabel: string;
  sentence: string;
  /** Everything the filter-bar search looks through (§3). */
  searchText: string;
}

export const CLOSED_OUTCOME_LABEL: Record<ClosedOutcome, { label: string; cls: string }> = {
  used: { label: 'Used', cls: 'b-ok' },
  expired: { label: 'Expired', cls: 'b-mute' },
  not_sent: { label: 'Not sent', cls: 'b-mute' },
  denied: { label: 'Denied', cls: 'b-bad' },
};

/** Options for the outcome <select> in the filter bar (§3 Pre-auths). */
export const CLOSED_OUTCOME_OPTIONS: { value: ClosedOutcome; label: string }[] = [
  { value: 'used', label: 'Used' },
  { value: 'expired', label: 'Expired' },
  { value: 'not_sent', label: 'Not sent' },
  { value: 'denied', label: 'Denied' },
];

const DENY_REASONS = [
  'the plan has a 12-month waiting period for implants',
  'a frequency limit had already been met this year',
  'the tooth was missing before the coverage started',
  'the submitted X-ray was older than 12 months',
];
const NOT_SENT_REASONS = [
  'the visit was cancelled before it was sent',
  'the patient moved the treatment out of this benefit year',
  'the office decided not to prepare it for this payer',
  'the patient’s coverage ended before the visit',
];

/** A short mono reference stub for a payer, e.g. "DEL-PA-48213". */
function referenceFor(r: () => number, payer: string): string {
  const stub = payer.replace(/[^A-Za-z]/g, '').slice(0, 3).toUpperCase();
  return `${stub}-PA-${String(int(r, 10000, 99999))}`;
}

export const CLOSED_TOTAL = 3600;

/** The full Closed history — deterministic, newest and oldest ~24 months apart. */
export function buildClosed(): ClosedPreauth[] {
  return generate(CLOSED_TOTAL, 0x9a2f31, (r, i) => {
    const patient = fullName(r);
    const payer = pick(r, PAYERS);
    const proc = pick(r, CDT);
    const tooth = `#${String(int(r, 1, 32))}`;
    const estimate = money(r, 180, 2400);
    const { iso, label } = pastDate(r, 24);

    const roll = r();
    const outcome: ClosedOutcome =
      roll < 0.46 ? 'used' : roll < 0.68 ? 'expired' : roll < 0.85 ? 'not_sent' : 'denied';

    const reference = outcome === 'not_sent' ? '' : referenceFor(r, payer);

    let sentence: string;
    switch (outcome) {
      case 'used':
        sentence = `ELVA added ${reference} to the claim on ${label}.`;
        break;
      case 'expired':
        sentence = `Approved by ${payer}, never used — the authorization lapsed on ${label}.`;
        break;
      case 'not_sent':
        sentence = `Not sent — ${pick(r, NOT_SENT_REASONS)}.`;
        break;
      case 'denied':
        sentence = `${payer} denied it — ${pick(r, DENY_REASONS)}.`;
        break;
    }

    const searchText = [patient, payer, reference, proc.code, proc.desc, tooth, String(Math.round(estimate)), estimate.toFixed(2)]
      .join(' ')
      .toLowerCase();

    return {
      id: `PA-C${String(i + 1).padStart(5, '0')}`,
      patient,
      payer,
      code: proc.code,
      procedure: proc.desc,
      tooth,
      reference,
      estimate,
      outcome,
      closedIso: iso,
      closedLabel: label,
      sentence,
      searchText,
    };
  });
}
